import { FirebaseService, ToastService } from "@shared/lib/services";
import { Button, Flex, Form, Input, Radio, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { Link, UploadCloud } from "lucide-react";
import { useEffect, useState } from "react";
import useAddAdRightsMediaLink from "../lib/hooks/useAddAdRightsMediaLink";
import styles from "./styles.module.scss";

export default function AddMediaLinkForm({
  adRightsTrackerId,
  initialMediaLink,
  onSuccess,
  onCancel,
}: {
  adRightsTrackerId: string;
  initialMediaLink?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}) {
  const [form] = Form.useForm();

  // Set initial media link if provided
  useEffect(() => {
    if (initialMediaLink) {
      form.setFieldsValue({ mediaLink: initialMediaLink });
    }
  }, [initialMediaLink, form]);
  const [activeTab, setActiveTab] = useState<"live" | "file">("live");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [isFirebaseUploading, setIsFirebaseUploading] = useState<boolean>(false);

  const {
    addAdRightsMediaLink,
    isAdRightsMediaLinkLoading,
    isAddAdRightsMediaSuccess,
    addAdRightsMediaLinkError,
  } = useAddAdRightsMediaLink();

  // Watch mutation success/failure
  useEffect(() => {
    if (isAddAdRightsMediaSuccess) {
      ToastService.success("Media added successfully.");
      form.resetFields();
      setFileList([]);
      setUploadedUrls([]);
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [isAddAdRightsMediaSuccess, onSuccess, form]);

  useEffect(() => {
    if (addAdRightsMediaLinkError) {
      ToastService.error("Failed to add media. Please try again.");
    }
  }, [addAdRightsMediaLinkError]);

  const uploadToFirebase = async (file: File) => {
    const url = await FirebaseService.uploadFileToFirebase(
      `ad-rights/${adRightsTrackerId}`,
      file
    );
    return url;
  };

  const uploadProps = {
    fileList,
    multiple: true,
    customRequest: async (options: any) => {
      const { file, onSuccess: onUploadSuccess, onError: onUploadError } = options;
      try {
        setIsFirebaseUploading(true);
        const firebaseUrl = await uploadToFirebase(file as File);
        setUploadedUrls((prev) => [...prev, firebaseUrl]);
        onUploadSuccess(firebaseUrl);
      } catch (err) {
        onUploadError(err);
        ToastService.error(`Failed to upload ${file.name}`);
      } finally {
        setIsFirebaseUploading(false);
      }
    },
    onChange: (info: any) => {
      setFileList(info.fileList);
    },
    onRemove: (file: UploadFile) => {
      const url = file.response as string;
      if (url) {
        setUploadedUrls((prev) => prev.filter((u) => u !== url));
      }
    },
    accept: ".mp4, .mov, .jpg, .png, image/jpeg, image/png, video/mp4, video/quicktime, image/gif, image/webp",
  };

  const handleSubmit = async (values: any) => {
    if (activeTab === "live") {
      const mediaLink = values.mediaLink?.trim();
      if (!mediaLink) {
        ToastService.error("Please enter a live link.");
        return;
      }
      addAdRightsMediaLink({
        adRightsTrackerId,
        mediaLink,
        downloadableMediaUrls: undefined,
      });
    } else {
      if (uploadedUrls.length === 0) {
        ToastService.error("Please upload at least one file.");
        return;
      }
      addAdRightsMediaLink({
        adRightsTrackerId,
        mediaLink: undefined, // First uploaded url as primary link
        downloadableMediaUrls: uploadedUrls, // All uploaded urls joined by comma
      });
    }
  };

  const isSubmitting = isAdRightsMediaLinkLoading || isFirebaseUploading;

  return (
    <div className={styles.formContainer}>
      <span className={styles.adRightsLabel}>Upload Media</span>
      <Radio.Group
        options={[
          { label: "Live Link", value: "live" },
          { label: "Upload File", value: "file" },
        ]}
        value={activeTab}
        onChange={(e) => {
          setActiveTab(e.target.value);
          form.resetFields();
          setFileList([]);
          setUploadedUrls([]);
        }}
        className={styles.radioGroup}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className={styles.form}
      >
        {activeTab === "live" ? (
          <Form.Item
            name="mediaLink"
            label={<span className={styles.formLabel}>Live Asset Link</span>}
            rules={[
              { required: true, message: "Please enter a valid link" },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          >
            <Input
              prefix={<Link size={14} style={{ opacity: 0.6 }} />}
              placeholder="https://instagram.com/p/... or https://youtube.com/..."
              className={styles.formInput}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label={<span className={styles.formLabel}>Upload Media Files</span>}
            help="MP4, MOV, JPG, PNG formats are supported"
          >
            <Upload.Dragger {...uploadProps} className={styles.dragger}>
              <p className={styles.uploadIcon}>
                <UploadCloud size={24} />
              </p>
              <p className={styles.uploadText}>Click or drag files here to upload</p>
              <p className={styles.uploadHint}>Support for video or image files</p>
            </Upload.Dragger>
          </Form.Item>
        )}

        <Flex gap={12} justify="flex-end" className={styles.formActions}>
          {onCancel && (
            <Button onClick={onCancel} disabled={isSubmitting} className={styles.cancelBtn}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            className={styles.submitBtn}
          >
            Submit
          </Button>
        </Flex>
      </Form>
    </div>
  );
}